// ===========================================
// Meet & Greet Booking Page (Public)
// ===========================================
// Fun, playful booking form for prospective parents
// Features piano header and character decorations

import { useState, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  FormHelperText,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Checkbox,
  FormControlLabel,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  MusicNote as MusicIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { addDays, format, startOfDay, isSameDay } from 'date-fns';
import { isTeacherAwayOnDay } from '../../utils/timeOff';
import { toLocalDateString } from '../../utils/dateUtils';
import {
  useSchoolInfo,
  useSchoolInstruments,
  useCreateMeetAndGreet,
  useMeetAndGreetAvailability,
} from '../../hooks/useMeetAndGreet';
import type { InstrumentOption } from '../../api/meetAndGreet.api';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../services/api';
import SchoolHomeButton from '../../components/public/SchoolHomeButton';
import PublicFooter from '../../components/public/PublicFooter';
import PublicPageShell from '../../components/public/PublicPageShell';
import TurnstileWidget from '../../components/public/TurnstileWidget';
import BookingSubmittedMessage from '../../components/public/BookingSubmittedMessage';
import { useTurnstileGate, isSpamCheckFailed } from '../../hooks/useTurnstileGate';

// ===========================================
// VALIDATION SCHEMA
// ===========================================

/** The server's limit on `enquiryToken` (BE `createMeetAndGreetSchema`). */
const ENQUIRY_TOKEN_MAX = 200;

const phoneRegex = /^(\+61|0)[2-478]\d{8}$|^\+?[\d\s-]{10,15}$/;

const bookingSchema = z.object({
  parentFirstName: z.string().min(1, 'First name is required').max(50),
  parentLastName: z.string().min(1, 'Last name is required').max(50),
  email: z.string().email('Invalid email address'),
  phone: z.string().regex(phoneRegex, 'Invalid phone number'),
  childFirstName: z.string().min(1, "Child's first name is required").max(50),
  childLastName: z.string().min(1, "Child's last name is required").max(50),
  childAge: z.number().min(3, 'Minimum age is 3').max(99, 'Maximum age is 99'),
  instrumentId: z.string().min(1, 'Please select an instrument'),
  notes: z.string().max(500).optional().or(z.literal('')),
});

type BookingFormData = z.infer<typeof bookingSchema>;

// ===========================================
// TIME SLOT TYPE
// ===========================================

interface TimeSlot {
  time: string;
  available: boolean;
}

// ===========================================
// COMPONENT
// ===========================================

export default function MeetAndGreetBookingPage() {
  const { schoolSlug } = useParams<{ schoolSlug: string }>();
  const navigate = useNavigate();
  // The office's link carries `?e=<signed token>` (enquiries Phase 3): passed through untouched so
  // the booking joins that enquiry. The page never says whether it linked, and a token the server
  // would refuse by length is left out, because a booking must never fail over it.
  const [searchParams] = useSearchParams();
  const rawEnquiryToken = searchParams.get('e');
  const enquiryToken = rawEnquiryToken && rawEnquiryToken.length <= ENQUIRY_TOKEN_MAX ? rawEnquiryToken : null;
  const effectiveSlug = schoolSlug || 'music-n-me';

  const [submitted, setSubmitted] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [legalModal, setLegalModal] = useState<'terms' | 'privacy' | null>(null);

  // Fetch school info, instruments, and availability
  const { data: schoolInfo, isLoading: loadingSchool, error: schoolError } = useSchoolInfo(effectiveSlug);

  // #412: Cloudflare Turnstile. The server verifies the token; this only collects it.
  const turnstile = useTurnstileGate(schoolInfo?.turnstileSiteKey);
  const [spamCheckFailed, setSpamCheckFailed] = useState(false);
  // false = the spam check could not run on the server, so no verification email went.
  const [verificationEmailSent, setVerificationEmailSent] = useState(true);
  const { data: instruments } = useSchoolInstruments(effectiveSlug);
  const { data: availability } = useMeetAndGreetAvailability(effectiveSlug);

  // Fetch legal settings (lazy - only when modal opens)
  const { data: legalData } = useQuery({
    queryKey: ['public-legal', effectiveSlug],
    queryFn: async () => {
      const res = await apiClient.get<{ status: string; data: { schoolName: string; termsOfService: string | null; privacyPolicy: string | null } }>(
        `/public/schools/${effectiveSlug}/legal`
      );
      return res.data;
    },
    enabled: legalModal !== null,
  });

  // Form mutation
  const createMutation = useCreateMeetAndGreet();

  // Form setup
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<BookingFormData>({
    resolver: zodResolver(bookingSchema),
    mode: 'onChange',
    defaultValues: {
      parentFirstName: '',
      parentLastName: '',
      email: '',
      phone: '',
      childFirstName: '',
      childLastName: '',
      childAge: 5,
      instrumentId: '',
      notes: '',
    },
  });

  const formValues = watch();

  // Generate available dates based on configured time slots
  const availableDates = useMemo(() => {
    if (!availability?.timeSlots || availability.timeSlots.length === 0) {
      return [];
    }

    // Get unique days of week that have active time slots
    const availableDaysOfWeek = new Set(
      availability.timeSlots
        .filter((slot) => slot.isActive)
        .map((slot) => slot.dayOfWeek)
    );

    const advanceBookingDays = availability?.settings?.advanceBookingDays || 14;
    const minimumNoticeHours = availability?.settings?.minimumNoticeHours || 24;

    const dates: Date[] = [];
    let currentDate = new Date();

    // Apply minimum notice hours
    currentDate.setHours(currentDate.getHours() + minimumNoticeHours);
    currentDate = addDays(startOfDay(currentDate), 0);

    // Start from tomorrow at minimum
    if (startOfDay(currentDate) <= startOfDay(new Date())) {
      currentDate = addDays(startOfDay(new Date()), 1);
    }

    const maxDate = addDays(new Date(), advanceBookingDays);

    while (currentDate <= maxDate && dates.length < 10) {
      const dayOfWeek = currentDate.getDay();
      // Only include days that have configured time slots
      if (availableDaysOfWeek.has(dayOfWeek)) {
        dates.push(startOfDay(currentDate));
      }
      currentDate = addDays(currentDate, 1);
    }

    return dates;
  }, [availability]);

  // Get time slots for selected date
  const timeSlots = useMemo(() => {
    if (!selectedDate || !availability?.teachers || !availability?.timeSlots) return [];

    // If no teachers are configured, show message
    if (availability.teachers.length === 0) return [];

    // Get day of week for selected date
    const selectedDayOfWeek = selectedDate.getDay();

    // Filter time slots for the selected day of week
    const dayTimeSlots = availability.timeSlots.filter(
      (slot) => slot.dayOfWeek === selectedDayOfWeek && slot.isActive
    );

    if (dayTimeSlots.length === 0) return [];

    // Generate unique time slots across all teachers
    // A slot is available if ANY teacher is free at that time
    const sessionDuration = availability.settings?.sessionDuration || 15;
    const bufferTime = availability.settings?.bufferTime || 0;
    const bookedSlots = availability.bookedSlots || [];
    const teachers = availability.teachers;
    const unavailablePeriods = availability.unavailablePeriods || [];
    const selectedDayString = toLocalDateString(selectedDate);
    const slotMap = new Map<string, boolean>();

    for (const configuredSlot of dayTimeSlots) {
      const [startHours, startMinutes] = configuredSlot.startTime.split(':').map(Number);
      const [endHours, endMinutes] = configuredSlot.endTime.split(':').map(Number);

      const rangeStartMinutes = startHours * 60 + startMinutes;
      const rangeEndMinutes = endHours * 60 + endMinutes;

      let currentMinutes = rangeStartMinutes;
      while (currentMinutes + sessionDuration <= rangeEndMinutes) {
        const hours = Math.floor(currentMinutes / 60);
        const minutes = currentMinutes % 60;
        const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

        // Check if at least one teacher is free at this time
        const anyTeacherFree = teachers.some((teacher) => {
          // A teacher on an APPROVED away period is not free that day at all.
          if (isTeacherAwayOnDay(unavailablePeriods, teacher.id, selectedDayString)) return false;

          const slotDateTime = new Date(selectedDate);
          slotDateTime.setHours(hours, minutes, 0, 0);

          const isBooked = bookedSlots.some((booked) => {
            if (!booked.dateTime || booked.teacherId !== teacher.id) return false;
            const bookedDate = new Date(booked.dateTime);
            return isSameDay(bookedDate, slotDateTime) &&
              bookedDate.getHours() === hours &&
              bookedDate.getMinutes() === minutes;
          });

          return !isBooked;
        });

        // Only update if this time hasn't been seen, or if a teacher is now available
        if (!slotMap.has(timeString)) {
          slotMap.set(timeString, anyTeacherFree);
        } else if (anyTeacherFree) {
          slotMap.set(timeString, true);
        }

        currentMinutes += sessionDuration + bufferTime;
      }
    }

    // Convert map to sorted array
    return Array.from(slotMap.entries())
      .map(([time, available]) => ({ time, available }))
      .sort((a, b) => a.time.localeCompare(b.time));
  }, [selectedDate, availability]);

  // Get selected instrument name
  const selectedInstrumentName = useMemo(() => {
    if (!formValues.instrumentId || !instruments) return null;
    return instruments.find((i: InstrumentOption) => i.id === formValues.instrumentId)?.name;
  }, [formValues.instrumentId, instruments]);

  const onSubmit = async (data: BookingFormData) => {
    if (!schoolInfo || !selectedDate || !selectedSlot) return;

    // Combine date and time
    const [hours, minutes] = selectedSlot.time.split(':').map(Number);
    const dateTime = new Date(selectedDate);
    dateTime.setHours(hours, minutes, 0, 0);

    setSpamCheckFailed(false);
    try {
      const parentFullName = `${data.parentFirstName} ${data.parentLastName}`.trim();
      const result = await createMutation.mutateAsync({
        schoolId: schoolInfo.id,
        studentFirstName: data.childFirstName,
        studentLastName: data.childLastName,
        studentAge: data.childAge,
        contact1Name: parentFullName,
        contact1Email: data.email,
        contact1Phone: data.phone.replace(/[\s-]/g, ''),
        contact1Relationship: 'Parent',
        emergencyName: parentFullName,
        emergencyPhone: data.phone.replace(/[\s-]/g, ''),
        emergencyRelationship: 'Parent',
        instrumentId: data.instrumentId,
        preferredDateTime: dateTime.toISOString(),
        additionalNotes: data.notes || undefined,
        turnstileToken: turnstile.tokenForSubmit,
        ...(enquiryToken ? { enquiryToken } : {}),
      });
      // `true` means the email was attempted; only `false` changes what the family is told.
      setVerificationEmailSent(result?.verificationEmailSent !== false);
      setSubmitted(true);
    } catch (error) {
      // Other errors (a duplicate booking, an existing family) are shown by the mutation;
      // a spam-check refusal is explained inline.
      if (isSpamCheckFailed(error)) setSpamCheckFailed(true);
      // A token is single use, and the server consumes it even when it then refuses
      // (a duplicate booking, an existing family): reset so a retry gets a fresh one.
      // A success unmounts the form, which removes the widget, so no reset is needed.
      turnstile.reset();
    }
  };

  // The summary follows the FORM; the spam check only holds the button (/qa Phase 6 L2:
  // waiting for a token used to read "Please fill in all required fields").
  const formComplete = Boolean(isValid && selectedDate && selectedSlot && agreedToTerms);
  const canSubmit = formComplete && turnstile.ready;

  // Loading state
  if (loadingSchool) {
    return (
      <Box sx={{ bgcolor: '#FCF6E6', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading...</Typography>
      </Box>
    );
  }

  // Error state
  if (schoolError || !schoolInfo) {
    return (
      <Box sx={{ bgcolor: '#FCF6E6', minHeight: '100vh', py: 8 }}>
        <Container maxWidth="md">
          <Alert severity="error">
            School not found or not accepting bookings. Please check the URL and try again.
          </Alert>
          <Button sx={{ mt: 2 }} onClick={() => navigate('/')}>
            Go Home
          </Button>
        </Container>
      </Box>
    );
  }

  // Success state
  if (submitted) {
    return (
      <Box sx={{ bgcolor: '#FCF6E6', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Container maxWidth="sm" sx={{ flex: 1, py: 8 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" color="primary" gutterBottom>
              Booking Submitted!
            </Typography>
            <BookingSubmittedMessage verificationEmailSent={verificationEmailSent} email={formValues.email} />
            <SchoolHomeButton variant="contained" />
          </Paper>
        </Container>
        <PublicFooter schoolName={schoolInfo?.name} schoolSlug={effectiveSlug} />
      </Box>
    );
  }

  return (
    <PublicPageShell
      title="Book a Meet & Greet"
      subtitle={`Schedule a free ${availability?.settings?.sessionDuration || 15}-minute introductory session. No account required!`}
      schoolName={schoolInfo?.name}
      schoolSlug={effectiveSlug}
    >

      <Container maxWidth="lg" sx={{ pb: 4, position: 'relative', zIndex: 1, flex: 1 }}>
        <Grid container spacing={3}>
          {/* Left Column: Your Information */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, bgcolor: 'transparent', boxShadow: 'none' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Your Information
              </Typography>

              <Box component="form" sx={{ mt: 2 }}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="parentFirstName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Parent/Guardian First Name"
                          fullWidth
                          required
                          error={!!errors.parentFirstName}
                          helperText={errors.parentFirstName?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="parentLastName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Parent/Guardian Last Name"
                          fullWidth
                          required
                          error={!!errors.parentLastName}
                          helperText={errors.parentLastName?.message}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Email Address"
                      type="email"
                      fullWidth
                      required
                      sx={{ mb: 2 }}
                      error={!!errors.email}
                      helperText={errors.email?.message || "We'll send a confirmation email"}
                    />
                  )}
                />

                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Phone Number"
                      fullWidth
                      required
                      placeholder="04XX XXX XXX"
                      sx={{ mb: 2 }}
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                    />
                  )}
                />

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="childFirstName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Child's First Name"
                          fullWidth
                          required
                          error={!!errors.childFirstName}
                          helperText={errors.childFirstName?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="childLastName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Child's Last Name"
                          fullWidth
                          required
                          error={!!errors.childLastName}
                          helperText={errors.childLastName?.message}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Controller
                  name="childAge"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Child's Age"
                      type="number"
                      fullWidth
                      required
                      inputProps={{ min: 3, max: 99 }}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                      sx={{ mb: 2 }}
                      error={!!errors.childAge}
                      helperText={errors.childAge?.message}
                    />
                  )}
                />

                <Controller
                  name="instrumentId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth required sx={{ mb: 2 }} error={!!errors.instrumentId}>
                      <InputLabel>Instrument of Interest</InputLabel>
                      <Select {...field} label="Instrument of Interest">
                        <MenuItem value="">Select an instrument</MenuItem>
                        {instruments?.map((inst: InstrumentOption) => (
                          <MenuItem key={inst.id} value={inst.id}>
                            {inst.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.instrumentId && (
                        <FormHelperText>{errors.instrumentId.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />

                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Additional Notes (Optional)"
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Any questions or special requests?"
                      error={!!errors.notes}
                      helperText={errors.notes?.message}
                    />
                  )}
                />

                {/* Terms Agreement */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      I agree to the{' '}
                      <MuiLink
                        component="button"
                        type="button"
                        variant="body2"
                        underline="hover"
                        onClick={(e) => { e.preventDefault(); setLegalModal('terms'); }}
                      >
                        Terms of Service
                      </MuiLink>
                      {' '}and{' '}
                      <MuiLink
                        component="button"
                        type="button"
                        variant="body2"
                        underline="hover"
                        onClick={(e) => { e.preventDefault(); setLegalModal('privacy'); }}
                      >
                        Privacy Policy
                      </MuiLink>
                    </Typography>
                  }
                  sx={{ mt: 1 }}
                />
              </Box>
            </Paper>
          </Grid>

          {/* Right Column: Date & Time Selection */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, bgcolor: 'transparent', boxShadow: 'none' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Select your preferred Date & Time
              </Typography>

              {/* Date Selection */}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                Available Dates
              </Typography>
              {availableDates.length === 0 ? (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  No available dates at this time. Please contact the school directly to arrange a meet & greet session.
                </Alert>
              ) : (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                {availableDates.map((date) => (
                  <Paper
                    key={date.toISOString()}
                    onClick={() => {
                      setSelectedDate(date);
                      setSelectedSlot(null);
                    }}
                    sx={{
                      p: 1.5,
                      textAlign: 'center',
                      cursor: 'pointer',
                      minWidth: 70,
                      border: 2,
                      borderColor: selectedDate && isSameDay(selectedDate, date)
                        ? 'primary.main'
                        : 'divider',
                      bgcolor: selectedDate && isSameDay(selectedDate, date)
                        ? 'primary.main'
                        : 'background.paper',
                      color: selectedDate && isSameDay(selectedDate, date)
                        ? 'white'
                        : 'text.primary',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: selectedDate && isSameDay(selectedDate, date)
                          ? 'primary.main'
                          : 'action.hover',
                      },
                    }}
                  >
                    <Typography variant="caption" sx={{ textTransform: 'uppercase', opacity: 0.8 }}>
                      {format(date, 'EEE')}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {format(date, 'd')}
                    </Typography>
                  </Paper>
                ))}
              </Box>
              )}

              {/* Time Slots */}
              {selectedDate ? (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Available Time Slots
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {format(selectedDate, 'EEEE, d MMMM yyyy')}
                  </Typography>

                  {timeSlots.length === 0 ? (
                    <Alert severity="warning">
                      No teachers are currently available for meet & greet sessions.
                      Please contact the school directly to arrange a meeting.
                    </Alert>
                  ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                    {timeSlots.map((slot, index) => (
                      <Paper
                        key={index}
                        onClick={() => slot.available && setSelectedSlot(slot)}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          textAlign: 'center',
                          cursor: slot.available ? 'pointer' : 'not-allowed',
                          opacity: slot.available ? 1 : 0.5,
                          border: 2,
                          borderColor: selectedSlot === slot
                            ? 'primary.main'
                            : 'rgba(0, 0, 0, 0.12)',
                          bgcolor: selectedSlot === slot
                            ? 'primary.main'
                            : 'transparent',
                          color: selectedSlot === slot
                            ? 'white'
                            : 'text.primary',
                          transition: 'all 0.2s',
                          '&:hover': slot.available ? {
                            borderColor: 'primary.main',
                            bgcolor: selectedSlot === slot
                              ? 'primary.main'
                              : 'rgba(255, 255, 255, 0.15)',
                          } : {},
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {format(new Date(`2000-01-01T${slot.time}`), 'h:mm a')}
                        </Typography>
                        {!slot.available && (
                          <Typography variant="caption" sx={{ opacity: 0.7 }}>
                            Booked
                          </Typography>
                        )}
                      </Paper>
                    ))}
                  </Box>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, fontStyle: 'italic' }}>
                    Dates and times are subject to teacher availability.
                  </Typography>
                </>
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Please select a date to see available time slots
                </Alert>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Booking Summary */}
        <Paper sx={{ mt: 3, p: 3, bgcolor: 'transparent', boxShadow: 'none' }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Booking Summary
          </Typography>

          {formComplete ? (
            <>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 3,
                  bgcolor: 'primary.50',
                  borderColor: 'primary.200',
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <PersonIcon fontSize="small" color="primary" />
                      <Typography variant="body2" color="text.secondary">Parent</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formValues.parentFirstName || formValues.parentLastName ? `${formValues.parentFirstName} ${formValues.parentLastName}`.trim() : '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <PersonIcon fontSize="small" color="primary" />
                      <Typography variant="body2" color="text.secondary">Child</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formValues.childFirstName || formValues.childLastName ? `${formValues.childFirstName} ${formValues.childLastName}`.trim() : '—'} ({formValues.childAge} years old)
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <MusicIcon fontSize="small" color="primary" />
                      <Typography variant="body2" color="text.secondary">Instrument</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedInstrumentName || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ScheduleIcon fontSize="small" color="primary" />
                      <Typography variant="body2" color="text.secondary">Date & Time</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedDate && selectedSlot
                        ? `${format(selectedDate, 'EEEE, d MMMM yyyy')} at ${format(
                            new Date(`2000-01-01T${selectedSlot.time}`),
                            'h:mm a'
                          )}`
                        : '—'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Divider sx={{ my: 3 }} />
            </>
          ) : (
            <Alert severity="info" sx={{ mb: 3 }}>
              Please fill in all required fields and select a date & time to see your booking summary.
            </Alert>
          )}

          {/* What to Expect */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 3,
              border: 2,
              borderColor: '#96DAC9',
              bgcolor: 'rgba(150, 218, 201, 0.15)',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              What to Expect
            </Typography>
            <List dense disablePadding>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <EmailIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary="You'll receive a confirmation email with a verification link" />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <ScheduleIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary="Please verify your email within 24 hours to confirm this booking" />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary={`The session is completely free with no obligation - ${availability?.settings?.sessionDuration || 15} minutes`} />
              </ListItem>
            </List>
          </Paper>

          <TurnstileWidget
            ref={turnstile.widgetRef}
            siteKey={schoolInfo.turnstileSiteKey}
            action="meet_and_greet"
            onToken={turnstile.onToken}
            onUnavailable={turnstile.onUnavailable}
          />
          {spamCheckFailed && (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="spam-check-failed">
              The spam check didn&apos;t load or complete, so your booking wasn&apos;t sent. Turn off any content
              blocker for this page and try again, or contact {schoolInfo?.name ?? 'the school'} directly.
            </Alert>
          )}

          {formComplete && turnstile.waiting && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="turnstile-waiting">
              Checking you&apos;re not a robot…
            </Typography>
          )}

          {/* Submit Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button variant="outlined" onClick={() => navigate(-1)}>
              ← Go Back
            </Button>
            <Button
              variant="contained"
              size="large"
              disabled={!canSubmit || createMutation.isPending}
              onClick={handleSubmit(onSubmit)}
            >
              {createMutation.isPending ? 'Submitting...' : 'Confirm Booking →'}
            </Button>
          </Box>
        </Paper>
      </Container>

      {/* Legal Document Modal */}
      <Dialog
        open={legalModal !== null}
        onClose={() => setLegalModal(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {legalModal === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
          <IconButton onClick={() => setLegalModal(null)} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {!legalData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Typography
              variant="body1"
              sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}
            >
              {(legalModal === 'terms' ? legalData.termsOfService : legalData.privacyPolicy) ||
                `No ${legalModal === 'terms' ? 'terms of service' : 'privacy policy'} has been published yet.`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLegalModal(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </PublicPageShell>
  );
}
